import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, AlertCircle, X } from 'lucide-react';
import { categoriesApi } from '../../services/api';
import { useWorkspace } from '../../context/WorkspaceContext';
import Dialog from '../../components/Dialog';
import './CategoriesSettings.css';

const PRESET_COLORS = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
    '#14b8a6', // teal
    '#64748b'  // slate
];

function CategoriesSettings() {
    const { currentWorkspace } = useWorkspace();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal / Form state
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [color, setColor] = useState(PRESET_COLORS[0]);
    const [submitting, setSubmitting] = useState(false);

    // Dialog state
    const [dialog, setDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'confirm',
        variant: 'default',
        onConfirm: null
    });

    const fetchCategories = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            setLoading(true);
            const response = await categoriesApi.list(currentWorkspace.id);
            setCategories(response.data.categories || []);
        } catch (err) {
            console.error('Failed to fetch categories:', err);
            setError('Failed to load categories');
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const showAlert = (title, message, variant = 'default') => {
        setDialog({
            isOpen: true,
            title,
            message,
            type: 'alert',
            variant,
            onConfirm: () => setDialog(prev => ({ ...prev, isOpen: false }))
        });
    };

    const handleOpenCreate = () => {
        setEditingCategory(null);
        setName('');
        setDescription('');
        setColor(PRESET_COLORS[0]);
        setShowModal(true);
    };

    const handleOpenEdit = (category) => {
        setEditingCategory(category);
        setName(category.name);
        setDescription(category.description || '');
        setColor(category.color || PRESET_COLORS[0]);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCategory(null);
        setName('');
        setDescription('');
        setColor(PRESET_COLORS[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            showAlert('Validation Error', 'Category name is required', 'danger');
            return;
        }

        setSubmitting(true);
        try {
            if (editingCategory) {
                // Update
                const response = await categoriesApi.update(editingCategory.id, {
                    name: trimmedName,
                    description,
                    color
                });
                setCategories(prev => prev.map(cat => cat.id === editingCategory.id ? response.data.category : cat));
            } else {
                // Create
                const response = await categoriesApi.create({
                    name: trimmedName,
                    description,
                    color
                });
                setCategories(prev => [...prev, response.data.category]);
            }
            handleCloseModal();
        } catch (err) {
            console.error('Category submission failed:', err);
            const errMsg = err.response?.data?.error || 'Failed to save category';
            showAlert('Error', errMsg, 'danger');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id, catName) => {
        setDialog({
            isOpen: true,
            title: 'Delete Category',
            message: `Are you sure you want to delete the category "${catName}"? This action cannot be undone and will unlink this category from all associated test goals.`,
            type: 'confirm',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await categoriesApi.delete(id);
                    setCategories(prev => prev.filter(c => c.id !== id));
                    setDialog(prev => ({ ...prev, isOpen: false }));
                } catch (err) {
                    console.error('Failed to delete category:', err);
                    showAlert('Error', 'Failed to delete category', 'danger');
                }
            }
        });
    };

    return (
        <div className="categories-settings">
            <div className="settings-header">
                <div>
                    <h2 className="settings-section-title">Test Goal Categories</h2>
                    <p className="settings-section-subtitle">
                        Organize and filter your workspace test goals using custom categorized badges.
                    </p>
                </div>
                <button className="btn btn-primary" onClick={handleOpenCreate}>
                    <Plus size={16} />
                    New Category
                </button>
            </div>

            {error && (
                <div className="error-alert">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                </div>
            )}

            {loading ? (
                <div className="loading-state">Loading categories...</div>
            ) : categories.length === 0 ? (
                <div className="empty-state-card">
                    <p>No categories defined for this workspace yet.</p>
                    <button className="btn btn-secondary" onClick={handleOpenCreate}>
                        Create a Category
                    </button>
                </div>
            ) : (
                <div className="categories-table-container">
                    <table className="categories-table">
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Description</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map((cat) => (
                                <tr key={cat.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '50%',
                                                    backgroundColor: cat.color || '#94a3b8'
                                                }}
                                            />
                                            <span
                                                style={{
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    backgroundColor: cat.color ? `${cat.color}20` : '#94a3b820',
                                                    color: cat.color || '#94a3b8'
                                                }}
                                            >
                                                {cat.name}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="desc-cell">{cat.description || <span className="no-desc">—</span>}</td>
                                    <td className="actions-cell">
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                className="icon-btn"
                                                onClick={() => handleOpenEdit(cat)}
                                                title="Edit"
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                            <button
                                                className="icon-btn icon-btn-danger"
                                                onClick={() => handleDelete(cat.id, cat.name)}
                                                title="Delete"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="category-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingCategory ? 'Edit Category' : 'Create Category'}</h3>
                            <button className="close-btn" onClick={handleCloseModal}>
                                <X size={18} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label">Category Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="e.g. Authentication, Shopping Cart"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    maxLength={100}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Description (Optional)</label>
                                <textarea
                                    className="form-input text-area"
                                    placeholder="Brief description of this category..."
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Badge Color</label>
                                <div className="color-presets">
                                    {PRESET_COLORS.map(c => (
                                        <button
                                            type="button"
                                            key={c}
                                            className={`color-btn ${color === c ? 'active' : ''}`}
                                            style={{ backgroundColor: c }}
                                            onClick={() => setColor(c)}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? 'Saving...' : 'Save Category'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <Dialog
                isOpen={dialog.isOpen}
                title={dialog.title}
                message={dialog.message}
                type={dialog.type}
                variant={dialog.variant}
                onConfirm={dialog.onConfirm}
                onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))}
            />
        </div>
    );
}

export default CategoriesSettings;
